import _ from 'lodash'

import i18n from '@/i18n/i18n'
import { RetailerStockInputService } from '@/services/admin/RetailerStockInputService'
import { build_query, errorMessage } from '@/utilities/helpers.js'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = [
  'retailer_id',
  'product_id',
  'brand_id',
  'unit_id',
  'input_between',
  'has_remaining_stocks',
  'invoice_status'
]

export const allowedIncludes = 'retailer,product,brand,unit,productUnit'

export const allowedAppends = 'total_reserved_quantity,is_deletable,is_updatable,update_state,is_commission_paid'

export const allowedFields = ['retailer:id,name,alias', 'product:id,name', 'brand:id,name', 'unit:id,name']

export const allowedSorts = '-input_at'

export const state = {
  inputs: null,
  meta: null,
  stockInput: {},
  stockOutputs: [],
  stockOutputsMeta: null,
  statistics: null,
  shortSaleItems: [],
  isCheckShowInStock: false,
  totalRemainingStockCost: null,
  retailer: null,
  availableStockInputs: null
}

export const mutations = {
  SET_INPUTS(state, inputs) {
    state.inputs = inputs.data
    state.meta = inputs.meta
  },
  SET_STOCK_INPUT(state, stockInput) {
    state.stockInput = stockInput.data
  },
  SET_STOCK_OUTPUTS(state, stockOutputs) {
    state.stockOutputs = stockOutputs.data
    state.stockOutputsMeta = stockOutputs.meta
  },
  SET_STOCK_INPUT_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  SET_SHORT_SALE_ITEMS(state, shortSaleItems) {
    state.shortSaleItems = shortSaleItems
  },
  SET_CHECK_SHOW_IN_STOCK(state, isCheckShowInStock) {
    state.isCheckShowInStock = isCheckShowInStock
  },
  SET_TOTAL_REMAINING_STOCK_COST(state, totalRemainingStockCost) {
    state.totalRemainingStockCost = totalRemainingStockCost
  },
  SET_RETAILER(state, retailer) {
    state.retailer = retailer
  },
  SET_AVAILABLE_STOCK_INPUTS(state, availableStockInputs) {
    state.availableStockInputs = availableStockInputs
  }
}

export const actions = {
  getInputs({ commit, dispatch }, query) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends, allowedFields)

      retailerStockInputService
        .getInputs(newQuery)
        .then(response => {
          commit('SET_INPUTS', response)
          commit(
            'SET_CHECK_SHOW_IN_STOCK',
            Object.keys(query).length > 0
              ? query.has_remaining_stocks
                ? query.has_remaining_stocks
                : state.isCheckShowInStock
              : state.isCheckShowInStock
          )
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.stock_inputs')
            })
          }

          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getInput({ commit, dispatch, state }, inputId) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .getStockInput(inputId)
        .then(response => {
          commit('SET_STOCK_INPUT', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.stock_details')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getInputStatistics({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .getStatistics(state.stockInput.id)
        .then(response => {
          commit('SET_STOCK_INPUT_STATISTICS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.stock_stats')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRemainingByUnit({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .getRemainingByUnit(params.stock_input_id, params.product_unit_id)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.stock_input_remaining')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  searchRetailer({ commit, dispatch }, query) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .searchRetailer(query)
        .then(response => {
          commit('SET_RETAILER', response.data[0])
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.stockable_retailers')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  create({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .create(params)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.save', {
              element: t('store.admin.retailer_stock_input.stock_input')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.save_error', {
                element: t('store.admin.retailer_stock_input.stock_inputs')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateRetailerStockInput({ state, dispatch }, stockInput) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()
      retailerStockInputService
        .updateRetailerStockInput(state.stockInput.id, stockInput)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.retailer_stock_input.stock_input')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.update_error', {
                element: t('store.admin.retailer_stock_input.stock_input')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteInput({ state, dispatch }, stockInput) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()
      retailerStockInputService
        .deleteRetailerStockInput(stockInput.id)
        .then(response => {
          state.inputs = _.reject(state.inputs, item => item.id === stockInput.id)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.retailer_stock_input.stock_input')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(reason => {
          const notification = {
            type: 'warning',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.retailer_stock_input.stock_input')
              }) + reason
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(reason.data)
        })
    })
  },
  exportStockInputs({ dispatch }, query) {
    let withInvoices = query?.parameter?.withInvoices

    let newQuery = withInvoices
      ? build_query(query.query, allowedFilters, allowedIncludes)
      : build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      retailerStockInputService
        .exportStockInputs(newQuery, withInvoices)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.retailer_stock_input.stock_inputs')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.exporting_error', {
                element: t('store.admin.retailer_stock_input.stock_inputs')
              }) +
              ' ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  transferToRetailer({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      new RetailerStockInputService()
        .transferToRetailer(params.inputId, params.form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer_stock_input.stock_transfered_to_the_retailer')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.admin.retailer_stock_input.failed_to_trasfer_the_stock_to_the_retailer') +
              ' ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getShortSaleItems({ commit, dispatch, state }, stockId) {
    return new Promise((resolve, reject) => {
      new RetailerStockInputService()
        .getShortSaleItems(stockId)
        .then(response => {
          commit('SET_SHORT_SALE_ITEMS', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.retailer_stock_input.short_sale_items')
              }) +
              ' ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  closeShortSales({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      new RetailerStockInputService()
        .closeShortSales(params.stock_input_id, params.retailer_short_sales)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer_stock_input.short_sale_items_closed')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: err.response.status === 406 ? 'warning' : 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getTotalRemainingStockCost({ commit, dispatch }, query) {
    commit('SET_TOTAL_REMAINING_STOCK_COST', null)
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()

      let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends, allowedFields)

      retailerStockInputService
        .getTotalRemainingStockCost(newQuery)
        .then(response => {
          commit('SET_TOTAL_REMAINING_STOCK_COST', response.total_remaining_stock)
          resolve(response.total_remaining_stock)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_stock_input.total_remaining_stock_cost')
            })
          }

          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  addInvoice({ commit, state, dispatch }, params) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()
      retailerStockInputService
        .createRetailerStockInputInvoice(params.stockId, params.data)
        .then(response => {
          commit('SET_STOCK_INPUT', response)
          const notification = {
            type: 'success',
            message: t('store.shared.save', {
              element: t('views.admin.retailer_stock_inputs.invoice.stock_input_invoice')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(reason => {
          const notification = {
            type: 'warning',
            message: errorMessage(reason)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(reason.data)
        })
    })
  },
  editInvoice({ commit, state, dispatch }, params) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()
      retailerStockInputService
        .editRetailerStockInputInvoice(params.stockId, params.data)
        .then(response => {
          commit('SET_STOCK_INPUT', response)
          const notification = {
            type: 'success',
            message: t('store.shared.save', {
              element: t('views.admin.retailer_stock_inputs.invoice.stock_input_invoice')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(reason => {
          const notification = {
            type: 'warning',
            message: errorMessage(reason)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(reason.data)
        })
    })
  },
  deleteInvoice({ commit, state, dispatch }, invoice) {
    return new Promise((resolve, reject) => {
      let retailerStockInputService = new RetailerStockInputService()
      retailerStockInputService
        .deleteRetailerStockInputInvoice({
          invoiceId: invoice.id,
          stockId: invoice.stockId
        })
        .then(response => {
          commit('SET_STOCK_INPUT', response)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('views.admin.retailer_stock_inputs.invoice.stock_input_invoice')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(reason => {
          const notification = {
            type: 'warning',
            message: errorMessage(reason)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(reason.data)
        })
    })
  },
  getAvailableStockInputs({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      new RetailerStockInputService()
        .getAvailableStockInputs(params)
        .then(response => {
          commit('SET_AVAILABLE_STOCK_INPUTS', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.retailer_stock_input.stock_inputs')
              }) +
              ' ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  closeShortSaleItemUsingMultipleStockInputs({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      new RetailerStockInputService()
        .closeShortSaleItemUsingMultipleStockInputs(params.retailer_short_sale_ids, params.retailer_stock_input_ids)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer_stock_input.short_sale_items_closed')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer_stock_input.short_sale_items_closing_error') + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
